import decode from 'jwt-decode';
import Keycloak from 'keycloak-js';
import { PROVIDERS } from "./properties";

const STORAGE_KEY_TOKEN = 'hub_token';
const STORAGE_KEY_USER = 'hub_user';
const STORAGE_KEY_PROVIDER = 'selected_provider_id';
const STORAGE_KEY_CHAIN = 'auth_chain_index';

let keycloakInstance = null;

export function init(callback) {
    const user = getUser();

    if (loggedIn() && user && user.fromKeycloak) {
        _reconnectKeycloak(callback);
        return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const savedProviderId = localStorage.getItem(STORAGE_KEY_PROVIDER);

    if (urlParams.has('code') && savedProviderId) {
        const provider = PROVIDERS.find(p => p.id === savedProviderId);
        if (provider) {
            _initKeycloakInstance(provider, callback);
            return;
        }
    }

    _runDiscoveryChain(callback);
}

export function login(token, user) {
    localStorage.setItem(STORAGE_KEY_TOKEN, token);
    localStorage.setItem(STORAGE_KEY_USER, JSON.stringify(user));
}

export function logout() {
    localStorage.removeItem(STORAGE_KEY_TOKEN);
    localStorage.removeItem(STORAGE_KEY_USER);
    localStorage.removeItem(STORAGE_KEY_PROVIDER);
    sessionStorage.removeItem(STORAGE_KEY_CHAIN);

    if (keycloakInstance) {
        keycloakInstance.logout({
            redirectUri: window.location.origin + '/login'
        });
    } else {
        window.location.replace('/login');
    }
}

export function loginWithKeycloak(provider) {
    sessionStorage.removeItem(STORAGE_KEY_CHAIN);

    const kc = new Keycloak({
        url: provider.url,
        realm: provider.realm,
        clientId: provider.clientId
    });

    kc.init({
        checkLoginIframe: true,
        pkceMethod: 'S256'
    }).then(() => {
        localStorage.setItem(STORAGE_KEY_PROVIDER, provider.id);
        kc.login({
            redirectUri: window.location.origin + extractRedirect()
        });
    });
}

export function getProviders() {
    return PROVIDERS;
}

export function getToken() {
    return localStorage.getItem(STORAGE_KEY_TOKEN);
}

export function getUser() {
    const u = localStorage.getItem(STORAGE_KEY_USER);
    return u ? JSON.parse(u) : null;
}

export function createConfig() {
    const token = getToken();
    return token ? { headers: { 'Authorization': 'Bearer ' + token } } : {};
}

export function loggedIn() {
    const token = getToken();
    return !!token && !isTokenExpired(token);
}

export function extractRedirect() {
    const params = new URLSearchParams(window.location.search);
    return params.get('redirectTo') || '/';
}

export function redirectToLogin() {
    const redirect = window.location.pathname + window.location.search;
    return `/login?redirectTo=${encodeURIComponent(redirect)}`;
}

function isTokenExpired(token) {
    try {
        const decoded = decode(token);
        return decoded.exp < (Date.now() / 1000) - 10;
    } catch (err) {
        return false;
    }
}

function _reconnectKeycloak(callback) {
    const savedProviderId = localStorage.getItem(STORAGE_KEY_PROVIDER);
    const provider = PROVIDERS.find(p => p.id === savedProviderId);

    if (!provider) {
        logout();
        return;
    }

    const kc = new Keycloak({
        url: provider.url,
        realm: provider.realm,
        clientId: provider.clientId
    });

    kc.init({
        onLoad: 'check-sso',
        pkceMethod: 'S256'
    }).then(authenticated => {
        if (authenticated) {
            _finalizeLogin(kc, provider);
            callback();
        } else {
            logout();
        }
    }).catch(err => {
        console.error("[Auth Debug] Reconnect ERROR:", err);
        logout();
    });
}

function _runDiscoveryChain(callback) {
    let chainIndex = parseInt(sessionStorage.getItem(STORAGE_KEY_CHAIN) || '0', 10);

    if (chainIndex >= PROVIDERS.length) {
        sessionStorage.removeItem(STORAGE_KEY_CHAIN);
        callback();
        return;
    }

    const provider = PROVIDERS[chainIndex];

    const tempKeycloak = new Keycloak({
        url: provider.url,
        realm: provider.realm,
        clientId: provider.clientId
    });

    tempKeycloak.init({
        onLoad: 'check-sso',
        pkceMethod: 'S256'
    }).then(authenticated => {
        if (authenticated) {
            sessionStorage.removeItem(STORAGE_KEY_CHAIN);
            _finalizeLogin(tempKeycloak, provider);
            callback();
        } else {
            sessionStorage.setItem(STORAGE_KEY_CHAIN, (chainIndex + 1).toString());
            window.location.reload();
        }
    }).catch(err => {
        console.error(`[Auth Debug] Error connecting to ${provider.label}`, err);
        sessionStorage.setItem(STORAGE_KEY_CHAIN, (chainIndex + 1).toString());
        window.location.reload();
    });
}

function _initKeycloakInstance(provider, callback, initOptions = {}) {
    const kc = new Keycloak({
        url: provider.url,
        realm: provider.realm,
        clientId: provider.clientId
    });

    const options = Object.assign({
        onLoad: 'check-sso',
        pkceMethod: 'S256'
    }, initOptions);

    kc.init(options).then(authenticated => {
        if (authenticated) {
            _finalizeLogin(kc, provider);
        }
        callback();
    }).catch(err => {
        console.error("[Auth Debug] Instance Init failed", err);
        callback();
    });
}

function _finalizeLogin(kc, provider) {
    keycloakInstance = kc;
    localStorage.setItem(STORAGE_KEY_PROVIDER, provider.id);

    const parsed = kc.tokenParsed;
    const company =
        parsed.company ||
        (parsed.attributes && parsed.attributes.company && parsed.attributes.company[0]);

    const user = {
        email: parsed.email,
        firstName: parsed.given_name,
        lastName: parsed.family_name,
        company,
        permissions:
            parsed.resource_access &&
            parsed.resource_access[provider.apiClientId]
                ? parsed.resource_access[provider.apiClientId].roles
                : [],
        can_open: false,
        fromKeycloak: true
    };

    login(kc.token, user);

    kc.onAuthLogout = () => {
        logout();
    };
}